/**
 * Every backend code the estimate screen shows, in millwork English.
 *
 * Two rules govern this file:
 *
 *  1. No spreadsheet cell addresses. The backend's result keys still carry the
 *     legacy suffixes (they are the API), but nothing an estimator reads may.
 *     Andy asks "why is this $9,400" — the answer is "overhead at 44.5% of the
 *     build cost", never a cell reference.
 *  2. One source of strings. The cost trace and the legacy results surface
 *     import the same constants, so the two can never drift into describing the
 *     same number two different ways.
 */

import { MATERIAL_PRICE_OVERRIDE_BASIS_LABELS } from "../../../state/lineItems";

/* ── Engine / geometry provenance ──────────────────────────────────────── */

export const ENGINE_LABELS = {
  rate_card_v1: "Rate-card pricing",
  legacy_formula: "Legacy formula pricing",
  capability_v1: "Capability pricing",
  custom_assembly: "Custom / one-off",
  rafter_tail_linked_v1: "Rafter-tail pricing trial (Oct 2026)",
};

export const GEOMETRY_LABELS = {
  flat_2d: "flat panel",
  face_area: "flat panel",
  column_wrap_4face: "column wrap, all four faces",
  column_wrap: "column wrap, all four faces",
  surface_area_3d: "3-D surface area",
  formula_type: "legacy geometry",
};

export const COST_STRATEGY_LABELS = {
  rate_card: "priced from the rate card",
  area_rate: "priced by area",
  sheet_yield: "priced by sheet yield",
  flat_price: "priced at a flat price",
  reference_anchor: "priced from a reference job",
  custom_assembly: "priced from purchased items and build inputs",
};

/* ── Labor ─────────────────────────────────────────────────────────────── */

/** What the per-department rates multiply against. */
export const LABOR_BASIS_WORDS = {
  sqft: "sq ft",
  lineal_ft: "lineal ft of run",
  face: "face sq ft",
  unit: "piece",
};

export const DEPARTMENT_LABELS = {
  cnc: "CNC",
  cut: "Cut",
  fab: "Fabrication",
  paint: "Paint",
};

export const DEPARTMENT_ORDER = ["cnc", "cut", "fab", "paint"];

/**
 * Which hourly rate the shop is being costed at.
 *
 * The selector that chose between these was retired 2026-08-18: burden IS the
 * labor cost per employee-hour, so it is simply "Shop labor rate" now. The
 * other two survive only to label a JobVersion saved before the retirement.
 */
export const LABOR_RATE_TYPE_LABELS = {
  burden: "Shop labor rate",
  island: "Island rate (legacy)",
  custom: "Custom rate (legacy)",
};

export const LABOR_OVERRIDE_LEAD =
  "Hours typed in by hand for this line. The rate card was not used.";

export const LABOR_OVERRIDE_CLEAR_HINT =
  "Empties the four hour fields and hands this line back to the shared rate card. Any hour you type — including 0 — marks the line as hand-entered.";

export const LABOR_CARD_MISSING_WARNING =
  "No rate card matches this category and size — no shop labor was charged on this line.";

export const LABOR_OVERRIDE_CARD_MISSING_WARNING =
  "No rate card matches this category — shop labor priced from your override.";

const LABOR_OVERRIDE_HOUR_FIELDS = [
  "cnc_hours",
  "cut_hours",
  "fab_hours",
  "finish_hours",
  "paint_hours",
];

/** An hour field counts as typed when it holds a real number — "" and null don't. */
const laborHourEntered = (source, field) => {
  const raw = source?.[field];
  if (raw === null || raw === undefined || raw === "") return false;
  return Number.isFinite(Number(raw));
};

const laborSourceIsManual = (source) =>
  String(source?.labor_source || "").toLowerCase() === "manual";

/**
 * Mirrors the engine (`_apply_line_item_labor_overrides`): an override is
 * *present* when the line is marked manual AND at least one hour field carries
 * an explicit number — a deliberate 0 included. A stale manual marker riding
 * along with blank fields is not an override; that line prices from the card.
 */
export const laborOverrideApplied = (inputData) =>
  laborSourceIsManual(inputData) &&
  LABOR_OVERRIDE_HOUR_FIELDS.some((field) =>
    laborHourEntered(inputData, field),
  );

/**
 * The loud case: a present override whose every department is zero, so the line
 * prices with no shop labor at all. Takes either a form-state line item or a
 * result's `input_data` — both carry `labor_source` and the hour fields.
 */
export const hasZeroLaborOverride = (source) =>
  laborOverrideApplied(source) &&
  LABOR_OVERRIDE_HOUR_FIELDS.every((field) => {
    const raw = source?.[field];
    return raw === null || raw === undefined || raw === "" || Number(raw) === 0;
  });

/** A negative typed hour is invalid, never a confirmable zero-labor choice. */
export const hasNegativeLaborOverride = (source) =>
  laborOverrideApplied(source) &&
  LABOR_OVERRIDE_HOUR_FIELDS.some((field) => {
    const raw = source?.[field];
    return raw !== null && raw !== undefined && raw !== "" && Number(raw) < 0;
  });

/** True only while the estimator has not yet said "yes, really, no shop labor". */
export const zeroLaborNeedsConfirmation = (lineItem) =>
  hasZeroLaborOverride(lineItem) && !lineItem?.zero_labor_confirmed;

/* ── Zero shop labor (a deliberate all-zero override) ─────────────────────
   Owner-ratified: a typed all-zero override really does price the line with no
   shop labor. The condition for it is one keystroke away from a typo, so it is
   loud everywhere it can be seen — and resolvable in one click, because an
   estimator who meant it should not be nagged for the rest of the job. */

export const ZERO_LABOR_HEADLINE = "This line will price with NO shop labor.";

export const ZERO_LABOR_BODY =
  "All four departments are overridden to 0 hours, so CNC, cut, fabrication and paint are each charged $0. Confirm if that is what you meant, or hand the line back to the rate card.";

export const ZERO_LABOR_CONFIRM_LABEL = "Confirm no shop labor";

export const ZERO_LABOR_CONFIRMED_NOTE =
  "No shop labor — confirmed for this line.";

export const ZERO_LABOR_RAIL_WARNING =
  "Shop labor is overridden to zero and not yet confirmed.";

export const CHILD_ZERO_LABOR_RAIL_WARNING =
  "a component's shop labor is overridden to zero and not yet confirmed.";

export const ZERO_LABOR_TRACE_WARNING =
  "Every department is overridden to 0 hours, so this line carries no shop labor. Nobody has confirmed that yet.";

export const ZERO_LABOR_TRACE_NOTE =
  "No shop labor — a confirmed override prices this line with zero shop hours.";

export const NEGATIVE_LABOR_OVERRIDE_WARNING =
  "A department has negative hours — this line cannot be priced until it is fixed.";

export const MATERIAL_INDEPENDENT_NOTE = (category) =>
  `Applies to every ${category || "line"} this size, whatever the material.`;

export const LABOR_FLOOR_NOTE = (hours) =>
  `Minimum shop time of ${hours} hr applied — the computed hours came in under the floor and were scaled up.`;

/* ── AI line composer ──────────────────────────────────────────────────── */

/**
 * The draft request is synchronous and takes 5-20 seconds. Silence for that
 * long reads as a hang and gets double-submitted, so the dialog narrates what
 * the server is actually doing — reading the shop manual, searching the
 * historical corpus, sizing parts, re-checking against the shop rules.
 */
export const COMPOSER_PROGRESS_STEPS = [
  "Reading the shop manual…",
  "Finding similar past work…",
  "Sizing parts and hours…",
  "Checking the numbers against code…",
];

export const COMPOSER_STEP_INTERVAL_MS = 3500;

export const AI_DRAFT_BADGE = "AI-DRAFTED";

/** Says the two things that matter: where it came from, and that it isn't priced. */
export const aiDraftExplainer = (evidenceCount) =>
  `Drafted by AI from ${evidenceCount} similar past line item${
    evidenceCount === 1 ? "" : "s"
  }. This draft is not included in the price until you accept it. Edit anything, then accept.`;

export const AI_DRAFT_TRACE_NOTE =
  "These inputs were drafted by AI and reviewed by the estimator. The prices are the engine's.";

export const AI_CANDIDATE_FILED =
  "Filed a rate-card candidate for owner review.";

export const AI_CANDIDATE_FAILED =
  "Couldn't file the rate-card candidate — the line is still accepted.";

/* ── Finish ────────────────────────────────────────────────────────────── */

export const FINISH_MODE_TEXT = {
  island_finish: "Painted by Island Millwork",
  prep_only: "Shipped ready for paint — no paint hours, no finish material",
};

export const FINISH_MODE_NONE = "No finish chosen";

export const FINISH_INSIDE_MATERIAL_NOTE =
  "Finish is priced as a material, so it sits inside the material figure.";

export const FINISH_RATE_MISSING_WARNING =
  "A finish was chosen but the rate card has no finish rate attached — $0 of finish was charged.";

/* ── Material resolution ───────────────────────────────────────────────── */

export const MATERIAL_AMBIGUOUS_WARNING =
  "More than one material fits this class at different prices — pick one.";

export const THICKNESS_FROM_CARD_NOTE =
  "(thickness came from the rate card default)";

/* ── Quoted price (typed-line material override) ─────────────────────────
   The fourth meaning, the fourth colour — see estimate.css's header block.
   teal / --est-quoted: a price a human quoted, not one the engine derived. */

export const QUOTED_PRICE_BADGE = "Quoted price";

export const QUOTED_PRICE_HELPER =
  "A quoted, lump-sum or specialty price for this line's material. The product type, rate card and shop labor are unchanged — only the material dollars come from here.";

/* The material caption's second half when a quoted price is in force. It has
   to say the catalog rate is inert AND what replaced it — "not used on this
   line" alone left the caption arguing with its own first half, which was
   still explaining how the sheet had been picked. */
export const QUOTED_PRICE_NOT_USED_SUFFIX =
  "— not used; this line prices from the quoted price.";

/* What the caption calls the catalog material once it is no longer pricing
   the line. Same words as the cost trace's "Catalog rate (not used)" node. */
export const CATALOG_RATE_LEAD = "Catalog rate";

export const QUOTED_PRICE_FILE_HELPER =
  "Files this quoted price against the family's rate-card candidate for Andy to review. It never prices another job on its own.";

export const QUOTED_PRICE_FILED = "Filed for review";

export const QUOTED_PRICE_FILE_FAILED = "Couldn't file — Retry";

export const QUOTED_PRICE_DEFAULT_HINT =
  "entered on this line — not from the material catalog";

export const STANDALONE_QUOTED_LINE_BADGE = "Quoted line";

export const STANDALONE_QUOTED_LINE_NOTE =
  "This line prices from its quoted price alone — no product type, so no rate card and no material catalog. Any shop hours on it are the ones typed in by hand.";

export const STANDALONE_QUOTED_LINE_HINT =
  "Optional while this line carries a quoted price — it will price from the quote alone. Pick a type when you want the rate card and material catalog to price it.";

/** Millwork English for each quoted-price basis, for trace sentences. */
export const QUOTED_PRICE_BASIS_WORDS = {
  per_unit: "each",
  per_line: "for the line",
  per_sqft: "sq ft",
  per_lin_ft: "lineal foot",
  per_board: "board",
  per_gallon: "gallon",
  per_lb: "pound",
};

/** The trace sentence for a quoted price, honest about the basis actually used. */
export function describeQuotedPriceFormula(
  override,
  calculatedSqft,
  formatCurrency,
) {
  if (!override) return null;
  const amount = Number(override.amount);
  if (!Number.isFinite(amount)) return null;
  if (override.basis === "per_line") {
    const perUnit = Number(override.amount_per_unit);
    const pieces =
      Number.isFinite(perUnit) && perUnit > 0
        ? Math.round(amount / perUnit)
        : null;
    return pieces
      ? `${formatCurrency(amount)} quoted for the line ÷ ${pieces} piece${
          pieces === 1 ? "" : "s"
        }`
      : `${formatCurrency(amount)} quoted for the line`;
  }
  if (override.basis === "per_sqft") {
    const sqft = Number(calculatedSqft);
    return Number.isFinite(sqft)
      ? `${sqft.toFixed(2)} sq ft × ${formatCurrency(amount)}/sq ft quoted`
      : `${formatCurrency(amount)}/sq ft quoted`;
  }
  // Lineal feet, boards, gallons and pounds all extend by the count the line
  // carries, which the engine holds — so this states the rate and its unit and
  // leaves the extension to the line total shown above it. Never invent a
  // count the payload did not give us.
  const word = QUOTED_PRICE_BASIS_WORDS[override.basis];
  if (word && override.basis !== "per_unit") {
    return `${formatCurrency(amount)} quoted per ${word}`;
  }
  return `${formatCurrency(amount)} quoted each`;
}

/** "Each" / "Sq Ft" / … for a basis key, never the raw key. */
export function describeQuotedPriceBasis(basis) {
  if (!basis) return null;
  return (
    MATERIAL_PRICE_OVERRIDE_BASIS_LABELS[basis] || humanizeCode(basis) || null
  );
}

/* ── Material failure (missing/ambiguous material) ────────────────────────
   Andy-confirmed: labor still prices when material fails. Never say "did not
   price" — the line DID price, just with $0 of material. */

export const MATERIAL_FAILURE_HEADLINE =
  "No material was charged on this line — shop labor still priced.";

export const materialFailureTraceNote = (laborOverridden) =>
  laborOverridden
    ? "Shop labor on this line priced from your override and is included below."
    : "Shop labor on this line priced from the rate card and is included below.";

export const MATERIAL_FAILURE_MISSING =
  "No material record matches this line's class — shop labor was charged, material was not. Pick a specific sheet, or enter a Quoted price.";

export const MATERIAL_FAILURE_AMBIGUOUS =
  "More than one material fits this class at different prices, so no material was charged. Pick a specific sheet, or enter a Quoted price.";

export const MATERIAL_FAILURE_RAIL_NOTE =
  "No material was charged on this line — shop labor still priced.";

/* ── Fallbacks ─────────────────────────────────────────────────────────── */

export const FALLBACK_REASON_TEXT = {
  no_matching_rate_card: LABOR_CARD_MISSING_WARNING,
  missing_sheet_inputs:
    "Sheet yield data was missing, so this line priced by area instead.",
  invalid_sheet_layout:
    "The sheet layout could not be worked out, so this line priced by area instead.",
  missing_flat_override:
    "The flat price was missing, so this line priced by area instead.",
  missing_reference_anchor:
    "The reference job was missing, so this line used standard area pricing.",
  reference_anchor_failed:
    "The reference job could not be scaled, so this line used fallback pricing.",
  invalid_dimensions: "The dimensions on this line could not be read.",
};

/** snake_case → a sentence, so a code never reaches the screen raw. */
export function humanizeCode(value) {
  if (!value) return "";
  const words = String(value).replace(/_/g, " ").trim();
  if (!words) return "";
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function describeFallbackReason(reason) {
  if (!reason) return null;
  return FALLBACK_REASON_TEXT[reason] || `${humanizeCode(reason)}.`;
}

export function describeEngine(engineVersion) {
  if (!engineVersion) return null;
  return ENGINE_LABELS[engineVersion] || humanizeCode(engineVersion);
}

export function describeGeometry(geometryMethod) {
  if (!geometryMethod) return null;
  return GEOMETRY_LABELS[geometryMethod] || humanizeCode(geometryMethod);
}

export function describeCostStrategy(costStrategy) {
  if (!costStrategy) return null;
  return (
    COST_STRATEGY_LABELS[costStrategy] ||
    humanizeCode(costStrategy).toLowerCase()
  );
}

/**
 * How the server picked the sheet this line was priced with.
 * `resolution` is the backend's material_resolution payload.
 */
export function describeMaterialResolution(resolution) {
  if (!resolution) return null;
  const materialClass = resolution.material_class || "chosen";
  switch (resolution.source) {
    case "explicit":
      return "Pinned to this exact sheet on the line";
    case "class_card_thickness":
      return `Rate-card default thickness in the ${materialClass} class`;
    case "class_nearest_thickness": {
      const requested = resolution.requested_thickness_in;
      return requested === null || requested === undefined
        ? `Closest thickness in the ${materialClass} class`
        : `Closest thickness to ${requested}" in the ${materialClass} class`;
    }
    case "class_default":
      return `Chosen automatically from the ${materialClass} class`;
    case "ambiguous":
      return MATERIAL_AMBIGUOUS_WARNING;
    case "unresolved":
    case "legacy":
      return "No material resolved";
    default:
      return humanizeCode(resolution.source);
  }
}

/* ── Job-level wording (shared with the legacy results surface) ─────────── */

export const JOB_LABELS = {
  buildCost: "Build cost",
  materials: "Materials",
  materialOrigination: "Material origination",
  materialFinancing: "Material financing",
  materialsAfterFinancing: "Materials after financing",
  shopLabor: "Shop labor",
  installation: "Installation",
  delivery: "Delivery",
  miscellaneous: "Miscellaneous",
  overhead: "Overhead",
  breakeven: "Cost to break even",
  profit: "Profit",
  salesTax: "Sales tax",
  taxedAmount: "Taxed amount",
  priceBeforeCommission: "Price before commission",
  commissions: "Commissions",
  salesCommission: "Sales commission",
  shopProfitSharing: "Shop profit sharing",
  quote: "Quote",
  passThrough: "Delivery, install and miscellaneous",
};

export const JOB_NOTES = {
  buildCost:
    "Everything the job costs before overhead, profit, tax and commission.",
  quote: "What the customer is quoted.",
};

/**
 * The heading over every copy-out that ends up in QuickBooks — the per-line
 * description/unit-price pair, and the whole-job rows. One string so the two
 * can never drift into naming the same errand two different ways.
 */
export const QBO_PASTE_HEADING = "Paste into QuickBooks";

/** What the whole-job rows are and where they are meant to land. */
export const QBO_JOB_EXPORT_NOTE =
  "One row per priced line — description, quantity, unit price. Paste into a spreadsheet or a QuickBooks estimate grid and each value lands in its own cell. Lines that did not price are left out.";

export const PROFIT_ON_PRICE_NOTE =
  "Profit is a share of the price, not a markup on cost.";

export const PROFIT_CLAMPED_WARNING =
  "The profit margin was above the 95% ceiling and was capped.";

/**
 * Ratified August 2026: pass-through costs are reported separately but they
 * DO carry overhead and profit. Do not reintroduce the old carve-out wording.
 */
export function passThroughNote(amountText) {
  return `Delivery, install and miscellaneous total ${amountText}. They are reported separately so you can see how much of the job is reimbursable rather than fabricated — but under the pricing rules ratified in August 2026 they do carry overhead and profit like every other cost.`;
}

/** Which install branch the backend ran. */
export function describeInstall(results) {
  const subcontractor = Number(results?.subcontractor_install_cost || 0);
  return subcontractor > 0
    ? "Quoted by the installing subcontractor"
    : "Install hours at the install labor rate";
}

/**
 * Which delivery branch the backend ran — and, when it is the default, what
 * that default IS. A flat fee nobody sees is a flat fee nobody questions, so
 * the amount is named here rather than left to be discovered.
 */
export function describeDelivery(results) {
  const overridden =
    results?.delivery_override_used !== null &&
    results?.delivery_override_used !== undefined;
  if (overridden) return "Custom delivery amount entered for this job";
  const defaultFee = Number(results?.delivery_default_fee);
  return Number.isFinite(defaultFee) && defaultFee > 0
    ? `Standard flat delivery fee — $${Number(defaultFee)} (default, no override entered)`
    : "Standard flat delivery fee (default, no override entered)";
}
